import { US_STATE_NAMES } from './us-states';

/**
 * Image preparation and text parsing for the camera scan on the VIN and plate
 * fields. Everything here is a pure function over a File or a raw OCR string —
 * the OCR call itself lives in OcrService.
 *
 * OCR on a phone photo is noisy, so the parsers do the heavy lifting: they pull
 * plausible candidates out of the recognized text and, for VINs, verify them
 * against the ISO 3779 check digit before handing anything back.
 */

export type ScanMode = 'vin' | 'plate';

/** ISO 3779. A scan shorter than this is a partial read, not a whole VIN. */
export const VIN_LENGTH = 17;

export interface VinScanResult {
  vin: string;
  /** False means the value is a best guess — the caller should warn the user. */
  checkDigitValid: boolean;
}

export interface PlateScanResult {
  plate: string;
  /** Two-letter code, only when a state name was legible on the plate. */
  state?: string;
}

/** A region of the source photo, in its own pixel coordinates. */
export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Text around this height reads best, and a modern phone photo is 3-4x wider
 * than this — downscaling costs nothing and saves seconds of CPU per scan.
 */
const MAX_EDGE = 1600;

/**
 * A cropped VIN band is wide and short, so capping its long edge would leave the
 * characters tiny. Size the short edge instead, within sane bounds.
 */
const CROP_TARGET_SHORT_EDGE = 500;
const CROP_MAX_UPSCALE = 3;
const CROP_MAX_LONG_EDGE = 3600;

/** Mean luminance below this reads as light-text-on-dark, e.g. etched glass. */
const DARK_IMAGE_THRESHOLD = 110;

/** Clip the extremes before stretching so a highlight can't flatten the rest. */
const CONTRAST_CLIP = 0.02;

/**
 * Decode the photo, apply its EXIF orientation, optionally crop to a region the
 * user drew, and scale the result to a sensible working size.
 *
 * Uncropped photos are only downscaled — a global contrast pass changes nothing
 * at best and corrupts a good read at worst. Dark crops are the exception: see
 * enhanceIfDark.
 */
export async function preprocessImage(file: File, crop?: CropRect): Promise<HTMLCanvasElement> {
  const source = await decodeImage(file);
  try {
    const region = clampCrop(crop, source.width, source.height);
    const scale = region === null
      ? Math.min(1, MAX_EDGE / Math.max(source.width, source.height))
      : cropScale(region);

    const sx = region?.x ?? 0;
    const sy = region?.y ?? 0;
    const sw = region?.width ?? source.width;
    const sh = region?.height ?? source.height;

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(sw * scale));
    canvas.height = Math.max(1, Math.round(sh * scale));

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      throw new Error('Canvas 2D context unavailable');
    }
    context.imageSmoothingQuality = 'high';
    context.drawImage(source as CanvasImageSource, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

    if (region !== null) {
      enhanceIfDark(context, canvas.width, canvas.height);
    }
    return canvas;
  } finally {
    if (typeof ImageBitmap !== 'undefined' && source instanceof ImageBitmap) {
      source.close();
    }
  }
}

/**
 * Widen the box slightly on every side before cropping. A box drawn a few pixels
 * inside the text clips the first or last character, and the recognizer then reads the
 * clipped image perfectly and confidently — "7ABC123" comes back as "ABC12" with
 * nothing to signal the loss. A little margin is much cheaper than that.
 */
const CROP_MARGIN = 0.03;

/** Keep a user-drawn box inside the photo, and ignore a degenerate one. */
function clampCrop(crop: CropRect | undefined, width: number, height: number): CropRect | null {
  if (!crop) {
    return null;
  }
  const marginX = crop.width * CROP_MARGIN;
  const marginY = crop.height * CROP_MARGIN;
  const left = Math.max(0, crop.x - marginX);
  const top = Math.max(0, crop.y - marginY);
  const right = Math.min(width, crop.x + crop.width + marginX);
  const bottom = Math.min(height, crop.y + crop.height + marginY);

  const w = right - left;
  const h = bottom - top;
  return w < 16 || h < 16
    ? null
    : { x: Math.floor(left), y: Math.floor(top), width: Math.round(w), height: Math.round(h) };
}

function cropScale(region: CropRect): number {
  const shortEdge = Math.min(region.width, region.height);
  const longEdge = Math.max(region.width, region.height);
  const wanted = CROP_TARGET_SHORT_EDGE / shortEdge;
  return Math.min(CROP_MAX_UPSCALE, CROP_MAX_LONG_EDGE / longEdge, Math.max(1, wanted));
}

/**
 * A VIN etched into glass or stamped into dark metal is light text on a dark
 * field — the inverse of what OCR models are trained on. When the crop is
 * predominantly dark, invert it and stretch the contrast. It is gated on
 * darkness because the same stretch applied to an ordinary well-lit label made
 * that read worse.
 */
function enhanceIfDark(context: CanvasRenderingContext2D, width: number, height: number): void {
  const image = context.getImageData(0, 0, width, height);
  const pixels = image.data;
  const histogram = new Uint32Array(256);
  let total = 0;

  for (let i = 0; i < pixels.length; i += 4) {
    const luminance = Math.round(0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2]);
    pixels[i] = luminance;
    histogram[luminance]++;
    total += luminance;
  }

  const pixelCount = pixels.length / 4;
  if (total / pixelCount >= DARK_IMAGE_THRESHOLD) {
    return;
  }

  const clip = Math.floor(pixelCount * CONTRAST_CLIP);
  let low = 0;
  let high = 255;
  for (let seen = 0; low < 255; low++) {
    seen += histogram[low];
    if (seen > clip) { break; }
  }
  for (let seen = 0; high > 0; high--) {
    seen += histogram[high];
    if (seen > clip) { break; }
  }

  const span = Math.max(1, high - low);
  for (let i = 0; i < pixels.length; i += 4) {
    const stretched = Math.min(255, Math.max(0, ((pixels[i] - low) * 255) / span));
    const inverted = Math.round(255 - stretched);
    pixels[i] = inverted;
    pixels[i + 1] = inverted;
    pixels[i + 2] = inverted;
  }
  context.putImageData(image, 0, 0);
}

/**
 * createImageBitmap applies EXIF rotation for us (iOS photos are almost always
 * rotated); the <img> path is the fallback for browsers without the option.
 */
async function decodeImage(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Fall through to the <img> path.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Could not decode the selected image'));
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ---------------------------------------------------------------------------
// VIN
// ---------------------------------------------------------------------------

/** I, O and Q are not legal in a VIN, so a read of one is really 1 or 0. */
const VIN_CONFUSIONS: Record<string, string> = { I: '1', O: '0', Q: '0' };

const VIN_BODY = /[A-HJ-NPR-Z0-9]{17}/;
const VIN_BODY_ALL = /[A-HJ-NPR-Z0-9]{17}/g;

/** ISO 3779 letter values; digits score as themselves. */
const VIN_LETTER_VALUES: Record<string, number> = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
  J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
  S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
};

const VIN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

/**
 * Pull a VIN out of recognized text, preferring a candidate whose check digit
 * validates. A candidate that fails is still returned, flagged, so the user has
 * something to correct instead of retyping all 17 characters.
 *
 * Deliberately does NOT try to "fix" a failing check digit by swapping the
 * glyphs OCR confuses (5/S, 8/B, …). Roughly one in eleven random strings passes
 * the check, so with a dozen swap candidates a repair lands on a wrong-but-
 * passing VIN more often than not — turning an honest "please verify this" into
 * a silently corrupted VIN that claims to be verified.
 */
export function extractVin(rawText: string): VinScanResult | null {
  const tokens = tokenize(rawText);
  const candidates: string[] = [];
  const add = (list: string[], value: string) => {
    if (!list.includes(value)) { list.push(value); }
  };

  for (const token of tokens) {
    for (const variant of [token, mapVinConfusions(token)]) {
      const match = variant.match(VIN_BODY);
      if (match) { add(candidates, match[0]); }
    }
  }

  for (const candidate of candidates) {
    if (isValidVinCheckDigit(candidate)) {
      return { vin: candidate, checkDigitValid: true };
    }
  }

  // The VIN may have been split by a stray space or a sticker line break. Gluing
  // the tokens back together also glues unrelated words together, so a spliced
  // candidate is only trustworthy with a passing check digit behind it.
  const spliced: string[] = [];
  for (const match of mapVinConfusions(tokens.join('')).match(VIN_BODY_ALL) ?? []) {
    add(spliced, match);
  }
  for (const candidate of spliced) {
    if (isValidVinCheckDigit(candidate)) {
      return { vin: candidate, checkDigitValid: true };
    }
  }

  // Nothing proven. Fall back to a candidate only if it still looks like a VIN,
  // so a failed read leaves the field empty instead of full of sticker text.
  const unverified = candidates.find(looksLikeVin);
  if (unverified) {
    return { vin: unverified, checkDigitValid: false };
  }

  // Nothing 17 long: hand back the longest plausible fragment to edit.
  const partial = tokens
    .map(mapVinConfusions)
    .filter(token => token.length >= 11 && /^[A-HJ-NPR-Z0-9]+$/.test(token) && looksLikeVin(token))
    .sort((a, b) => b.length - a.length)[0];
  return partial ? { vin: partial, checkDigitValid: false } : null;
}

/**
 * ISO 3779 reserves the last six characters for the sequential production
 * number, which is numeric for any manufacturer building more than a handful of
 * vehicles a year. Without this, a line of sticker boilerplate is a perfectly
 * good VIN candidate — "GVWR1950KGTYPEPAS" is 17 legal characters.
 */
function looksLikeVin(candidate: string): boolean {
  return (candidate.slice(-6).match(/\d/g) ?? []).length >= 3;
}

/** ISO 3779: weighted sum of transliterated characters, mod 11, against position 9. */
export function isValidVinCheckDigit(vin: string): boolean {
  if (vin.length !== VIN_LENGTH) {
    return false;
  }
  let sum = 0;
  for (let i = 0; i < VIN_LENGTH; i++) {
    const character = vin[i];
    const value = /\d/.test(character) ? Number(character) : VIN_LETTER_VALUES[character];
    if (value === undefined) {
      return false;
    }
    sum += value * VIN_WEIGHTS[i];
  }
  const remainder = sum % 11;
  return vin[8] === (remainder === 10 ? 'X' : String(remainder));
}

function mapVinConfusions(token: string): string {
  return token.replace(/[IOQ]/g, character => VIN_CONFUSIONS[character]);
}

// ---------------------------------------------------------------------------
// License plate
// ---------------------------------------------------------------------------

/** Words that share the plate with the number and must never be mistaken for it. */
const PLATE_NOISE = new Set([
  'DMV', 'EXP', 'EXPIRES', 'REG', 'USA', 'US', 'STATE', 'PLATE', 'COUNTY', 'TAG',
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'SEPT', 'OCT', 'NOV', 'DEC',
  'THE', 'OF', 'AND', 'COM', 'ORG',
]);

const PLATE_MIN = 2;
const PLATE_MAX = 8;

/**
 * Pick the plate number out of recognized text, and the issuing state when its
 * name is legible. A plate number is short, mixes letters and digits more often
 * than not, and shares the plate with a state name, a slogan and a sticker year.
 */
export function extractPlate(rawText: string): PlateScanResult | null {
  const state = detectState(rawText);
  const stateWords = new Set(state ? (US_STATE_NAMES[state] ?? '').toUpperCase().split(' ') : []);

  const candidates = tokenize(rawText).filter(token =>
    token.length >= PLATE_MIN
    && token.length <= PLATE_MAX
    && !PLATE_NOISE.has(token)
    && !stateWords.has(token)
    && !isRegistrationYear(token),
  );
  if (candidates.length === 0) {
    return null;
  }

  const best = candidates.sort((a, b) => plateScore(b) - plateScore(a))[0];
  return state ? { plate: best, state } : { plate: best };
}

/** Mixed letters-and-digits looks like a plate; length breaks the tie. */
function plateScore(token: string): number {
  const mixed = /\d/.test(token) && /[A-Z]/.test(token);
  return (mixed ? 100 : 0) + token.length;
}

/** A bare 4-digit number on a plate is the registration sticker's year. */
function isRegistrationYear(token: string): boolean {
  return /^\d{4}$/.test(token) && Number(token) >= 1990 && Number(token) <= 2100;
}

/**
 * Match on full state names only. Two-letter codes appear too readily inside
 * plate numbers themselves to be safe to key on.
 */
function detectState(rawText: string): string | undefined {
  // Padded so a name at the very start or end still matches on word boundaries.
  const haystack = ` ${rawText.toUpperCase().replace(/[^A-Z]+/g, ' ').trim()} `;
  for (const [code, name] of Object.entries(US_STATE_NAMES)) {
    if (haystack.includes(` ${name.toUpperCase()} `)) {
      return code;
    }
  }
  return undefined;
}

// ---------------------------------------------------------------------------

/** Recognized text to uppercase alphanumeric runs, longest first. */
function tokenize(rawText: string): string[] {
  return (rawText.toUpperCase().match(/[A-Z0-9]+/g) ?? []).sort((a, b) => b.length - a.length);
}
